/**
 * AI credits in AI Studio (backend controller/credits, hook useAiCredits):
 * - AnswerLevelPicker (Profile tab): how capable the agent answers visitors; Ultimo Core /
 *   Pro / Max (keys standard / advanced / expert; Haiku / Sonnet / Opus 5.5, never shown) at
 *   1 / 4 / 8 credits per answer. Free agents answer on Core.
 * - MoLevelMenu (in the input, next to send): Mo's level; Mo costs what its work really costs.
 * - CreditsChip: the credits left, a click leads to billing (packs, upgrade).
 * All render nothing while credits are off for the agent (the platform still counts messages).
 * Both save at once (Robert, 16.10.2026): a dearer level after a dialog that says what it costs, a cheaper one directly.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt, faCheck, faChevronDown, faCoins, faGem, faLock, faWandMagicSparkles } from '@fortawesome/free-solid-svg-icons';
import useAiCredits, { creditsOn, saveAiCreditLevels } from '../../hooks/useAiCredits';
import { ConfirmDialog } from './PackageLibrary';

const LEVEL_ICONS = { standard: faBolt, advanced: faWandMagicSparkles, expert: faGem };

function useNumber() {
  const { i18n } = useTranslation();
  return (n) => new Intl.NumberFormat(i18n.language || 'en').format(Math.round(Number(n) || 0));
}

// a translated text with <b>…</b> around what matters (the credits), shown bold
const withBold = (text) => text.split(/<b>(.*?)<\/b>/).map((part, i) => (i % 2 ? <b key={i}>{part}</b> : part));

function creditsLeft(t, n, num) {
  return Number(n) === 1 ? t('agent_studio_credits_left_one') : t('agent_studio_credits_left', { count: num(n) });
}

/** Profile tab: the intelligence level of the agent's answers; onSaved tells the tab's save bar. */
export function AnswerLevelPicker({ botId, onSaved = null }) {
  const data = useAiCredits(botId);
  return creditsOn(data) ? <AnswerLevels botId={botId} data={data} onSaved={onSaved} /> : null;
}

function AnswerLevels({ botId, data, onSaved }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // a dearer level waiting for the owner's yes

  const current = data.answer_level;
  const currentLevel = data.answer_levels.find((l) => l.key === current);
  const pick = (level) => {
    if (level.key === current || busy) return;
    if (!level.allowed) {
      navigate(`/billing/${botId}`);
      return;
    }
    if (currentLevel && level.credits > currentLevel.credits) setConfirm(level);
    else apply(level);
  };
  const apply = async (level) => {
    setBusy(level.key);
    setError('');
    try {
      await saveAiCreditLevels(botId, { answer_level: level.key });
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.status === 402 ? t('agent_studio_level_locked') : e.message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="as-levels">
      <div className="as-levels-head">
        <span className="as-levels-title">{t('agent_studio_level_answer_title')}</span>
        <span className="as-levels-hint">{t('agent_studio_level_answer_hint')}</span>
      </div>
      <div className="as-levels-grid" role="radiogroup" aria-label={t('agent_studio_level_answer_title')}>
        {data.answer_levels.map((lv) => {
          const active = lv.key === current;
          return (
            <button key={lv.key} type="button" role="radio" aria-checked={active} disabled={!!busy}
              className={`as-level ${active ? 'is-active' : ''} ${lv.allowed ? '' : 'is-locked'}`} onClick={() => pick(lv)}>
              {/* like Mo's level menu: the icon as tall as name + description, the credits next to the name */}
              <span className="as-level-icon"><FontAwesomeIcon icon={LEVEL_ICONS[lv.key]} /></span>
              <span className="as-level-text">
                <span className="as-level-name">
                  {t(`agent_studio_level_${lv.key}`)}
                  <span className="as-level-price">
                    {lv.credits === 1 ? t('agent_studio_per_answer_one') : t('agent_studio_per_answer', { count: lv.credits })}
                  </span>
                </span>
                <span className="as-level-desc">{t(`agent_studio_level_${lv.key}_desc`)}</span>
              </span>
              {lv.allowed
                ? (active && <FontAwesomeIcon icon={faCheck} className="as-level-check" />)
                : <span className="as-level-lock"><FontAwesomeIcon icon={faLock} /> {t('agent_studio_paid_only')}</span>}
            </button>
          );
        })}
      </div>
      {error && <div className="as-error">{error}</div>}
      <ConfirmDialog open={!!confirm} title={t('agent_studio_upgrade_answer_title')}
        text={confirm ? withBold(t('agent_studio_upgrade_answer_text', {
          name: t(`agent_studio_level_${confirm.key}`), to: confirm.credits, from: currentLevel?.credits,
        })) : ''}
        action={confirm ? t('agent_studio_upgrade_confirm', { name: t(`agent_studio_level_${confirm.key}`) }) : ''}
        onClose={() => setConfirm(null)} onConfirm={() => apply(confirm)} />
    </div>
  );
}

/** In the input, left of the send button (like ChatGPT's model picker): Mo's level, as a compact menu. */
export function MoLevelMenu({ botId }) {
  const data = useAiCredits(botId);
  return creditsOn(data) ? <MoLevels botId={botId} data={data} /> : null;
}

function MoLevels({ botId, data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const [confirm, setConfirm] = useState(null); // a dearer level waiting for the owner's yes
  const num = useNumber();
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);

  const currentLevel = data.builder_levels.find((l) => l.key === data.builder_level);
  const pick = (lv) => {
    if (!lv.allowed) {
      navigate(`/billing/${botId}`);
      return;
    }
    setOpen(false);
    if (lv.key === data.builder_level) return;
    if (currentLevel && lv.run_cap_credits > currentLevel.run_cap_credits) setConfirm(lv);
    else apply(lv);
  };
  const apply = async (lv) => {
    setError('');
    try {
      await saveAiCreditLevels(botId, { builder_level: lv.key });
    } catch (e) {
      setError(e.status === 402 ? t('agent_studio_level_locked') : e.message);
    }
  };

  const levelName = t(`agent_studio_level_${data.builder_level}`);
  return (
    <div className="as-mo-level" ref={box}>
      <button type="button" className={`as-mo-level-btn ${open ? 'is-open' : ''}`} aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen((v) => !v)} title={t('agent_studio_mo_level_title')}
        aria-label={`${t('agent_studio_mo_level_title')}: ${levelName}`}>
        <FontAwesomeIcon icon={LEVEL_ICONS[data.builder_level]} className="as-mo-level-icon" />
        <span className="as-mo-level-label">{levelName}</span>
        <FontAwesomeIcon icon={faChevronDown} className="as-mo-level-caret" />
      </button>
      {open && (
        <div className="as-mo-menu" role="menu">
          <div className="as-mo-menu-head">
            <strong>{t('agent_studio_mo_level_title')}</strong>
            <span>{t('agent_studio_mo_level_hint')}</span>
          </div>
          {data.builder_levels.map((lv) => (
            <button key={lv.key} type="button" role="menuitemradio" aria-checked={lv.key === data.builder_level}
              className={`as-mo-option ${lv.key === data.builder_level ? 'is-active' : ''} ${lv.allowed ? '' : 'is-locked'}`}
              onClick={() => pick(lv)}>
              <span className="as-level-icon"><FontAwesomeIcon icon={LEVEL_ICONS[lv.key]} /></span>
              <span className="as-mo-option-text">
                <span className="as-mo-option-name">{t(`agent_studio_level_${lv.key}`)}</span>
                <span className="as-mo-option-desc">{t(`agent_studio_mo_${lv.key}_desc`)}</span>
              </span>
              {lv.allowed
                ? (lv.key === data.builder_level && <FontAwesomeIcon icon={faCheck} className="as-level-check" />)
                : <span className="as-level-lock"><FontAwesomeIcon icon={faLock} /> {t('agent_studio_paid_only')}</span>}
            </button>
          ))}
        </div>
      )}
      {error && <span className="as-mo-level-error">{error}</span>}
      <ConfirmDialog open={!!confirm} title={t('agent_studio_upgrade_mo_title')}
        text={confirm ? withBold(t('agent_studio_upgrade_mo_text', {
          name: t(`agent_studio_level_${confirm.key}`), to: num(confirm.run_cap_credits), from: num(currentLevel?.run_cap_credits),
        })) : ''}
        action={confirm ? t('agent_studio_upgrade_confirm', { name: t(`agent_studio_level_${confirm.key}`) }) : ''}
        onClose={() => setConfirm(null)} onConfirm={() => apply(confirm)} />
    </div>
  );
}

/** The credits left; a click leads to billing. */
export function CreditsChip({ botId }) {
  const data = useAiCredits(botId);
  return creditsOn(data) ? <Chip botId={botId} data={data} /> : null;
}

function Chip({ botId, data }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const num = useNumber();
  const low = data.available <= Math.max(5, (data.plan_allowance || 0) * 0.1);
  return (
    <button type="button" className={`as-credits-chip ${low ? 'is-low' : ''}`} onClick={() => navigate(`/billing/${botId}`)}
      title={t('agent_studio_credits_add')}>
      <FontAwesomeIcon icon={faCoins} />
      <span>{creditsLeft(t, Math.max(0, data.available), num)}</span>
    </button>
  );
}
